import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Heart, Users, Award, Globe } from "lucide-react";
import LeadForm from "../components/LeadForm";
import Seo from "../components/Seo";

const TEAM = [
  { name: "Rajesh Verma", role: "Founder & CEO", image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80" },
  { name: "Neha Kapoor", role: "Head of Operations", image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&q=80" },
  { name: "Vikram Iyer", role: "Visa Specialist", image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80" },
  { name: "Sanya Malhotra", role: "Honeymoon Expert", image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80" },
];

export default function About() {
  return (
    <div className="pt-24 lg:pt-32">
      <Seo title="About Us" path="/about" description="Acchajee Travels — crafting unforgettable journeys since 2012. IATA-approved travel agency in Gurugram, trusted by 50,000+ travellers across 150+ destinations." />
      <section className="container-p">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
          <span className="section-label">About Acchajee Travels</span>
          <h1 className="h1 mt-3">Crafting unforgettable journeys since 2012</h1>
          <p className="muted mt-6 text-lg leading-relaxed">What started as a small family-run travel desk in Gurugram is today one of India's most-loved travel brands — trusted by families, couples, corporates and B2B partners across the country.</p>
        </motion.div>
      </section>

      <section className="container-p mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
          <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80" alt="Travellers" className="w-full h-full object-cover" />
        </div>
        <div>
          <h2 className="h2 mb-5">Our Story</h2>
          <p className="muted leading-relaxed mb-4">Acchajee Travels was born out of a simple belief: travel should feel effortless, honest and personal. Over 15+ years, we've grown from serving a handful of Gurugram families to designing 5000+ trips a year across 150+ destinations.</p>
          <p className="muted leading-relaxed">From the shikaras of Dal Lake to the overwater villas of the Maldives, every itinerary we craft comes with the warmth of a family and the precision of a specialist.</p>
        </div>
      </section>

      <section className="section">
        <div className="container-p grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Mission", d: "To make world-class travel experiences accessible, honest and personal for every Indian family." },
            { icon: Eye, title: "Vision", d: "To be India's most trusted travel partner — the first name that comes to mind when you dream of getting away." },
            { icon: Heart, title: "Values", d: "Warmth, transparency, expertise. No hidden charges, no scripted itineraries — just travel done right." },
          ].map((c) => (
            <div key={c.title} className="card-glass p-8">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-5"><c.icon className="w-6 h-6 text-[#FF7D00]" strokeWidth={1.5} /></div>
              <h3 className="h3 mb-3">{c.title}</h3>
              <p className="muted">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section bg-slate-50">
        <div className="container-p">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Users, k: "50K+", v: "Travellers" },
              { icon: Globe, k: "150+", v: "Destinations" },
              { icon: Award, k: "15+", v: "Years" },
              { icon: Heart, k: "98%", v: "Satisfaction" },
            ].map((s) => (
              <div key={s.v} className="p-6">
                <s.icon className="w-8 h-8 text-[#006CE4] mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-display text-3xl font-bold text-[#0A2540]">{s.k}</div>
                <div className="text-sm muted">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section container-p">
        <div className="mb-10">
          <span className="section-label">Team</span>
          <h2 className="h2 mt-2">Meet the humans behind your holidays</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAM.map((t) => (
            <div key={t.name} className="text-left">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-4"><img src={t.image} alt={t.name} className="w-full h-full object-cover" /></div>
              <div className="font-display font-semibold text-[#0A2540]">{t.name}</div>
              <div className="text-sm muted">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-p mb-20">
        <div className="card-glass p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="h2 mb-3">Ready to start planning?</h2>
            <p className="muted">Share a few details and our travel expert will call you back within 30 minutes.</p>
          </div>
          <LeadForm compact source="about-page" />
        </div>
      </section>
    </div>
  );
}
